import { v4 as uuidv4 } from 'uuid';
import { normalizeHexColor } from './richText.js';
import { normalizeStoredImageItem, revokeLocalImageUrl } from './localImage.js';
import { LOCAL_ASSET } from './localAssets.js';
import { getQiniuSignedUrl } from './qiniuMedia.js';

export const HEADER_CAROUSEL_COMPONENT_TYPE = 'header-carousel';
export const HEADER_CAROUSEL_IMAGE_COMPONENT_TYPE = 'header-carousel-image';
export const HEADER_CAROUSEL_MODULE_TYPE = 'carousel';

export const HEADER_CAROUSEL_VARIANT_BANNER = 'banner';
export const HEADER_CAROUSEL_VARIANT_IMAGE = 'image';

export const HEADER_CAROUSEL_DEFAULT_HEIGHT = 300;
export const HEADER_CAROUSEL_DEFAULT_BORDER_RADIUS = 20;
export const HEADER_CAROUSEL_DEFAULT_AUTOPLAY_INTERVAL = 4000;
export const HEADER_CAROUSEL_MAX_SLIDES = 9;

const DEFAULT_TITLE_COLOR = '#ffffff';
const DEFAULT_SUBTITLE_COLOR = '#ffffff';
const DEFAULT_DOT_COLOR = '#cccccc';
const DEFAULT_DOT_ACTIVE_COLOR = '#333333';

const SLIDE_IMAGE_RANDOMS = [0, 1];

const BANNER_SLIDE_TEXTS = [
	['小红书新号起盘资料包', '定位、选题、封面与30天发布计划'],
	['小红书稳定更新运营手册', '内容排期、数据复盘与账号优化方法']
];

const LOCAL_CAROUSEL = LOCAL_ASSET.carousel;
const LOCAL_CAROUSEL_IMAGE = LOCAL_ASSET.carouselImage;

function createSlideImage(index = 0, variant = HEADER_CAROUSEL_VARIANT_BANNER) {
	const source = variant === HEADER_CAROUSEL_VARIANT_IMAGE
		? LOCAL_CAROUSEL_IMAGE
		: LOCAL_CAROUSEL;
	return {
		id: `header-carousel-image-${uuidv4()}`,
		filename: 'banner.jpg',
		previewUrl: source[index % source.length],
		url: '',
		local: false,
	};
}

export function createDefaultHeaderCarouselSlide(index = 0, variant = HEADER_CAROUSEL_VARIANT_BANNER) {
	const texts = BANNER_SLIDE_TEXTS[index] || ['', ''];
	return {
		id: uuidv4(),
		image: createSlideImage(index, variant),
		jumpLink: null,
		title: variant === HEADER_CAROUSEL_VARIANT_BANNER ? texts[0] : '',
		subtitle: variant === HEADER_CAROUSEL_VARIANT_BANNER ? texts[1] : '',
	};
}

export function createDefaultHeaderCarouselSlides(count = 3, variant = HEADER_CAROUSEL_VARIANT_BANNER) {
	const slides = [];
	for (let i = 0; i < count; i += 1) {
		slides.push(createDefaultHeaderCarouselSlide(i, variant));
	}
	return slides;
}

export function getHeaderCarouselBannerDefaults() {
	return normalizeHeaderCarouselData({
		variant: HEADER_CAROUSEL_VARIANT_BANNER,
		items: createDefaultHeaderCarouselSlides(2, HEADER_CAROUSEL_VARIANT_BANNER),
	});
}

export function getHeaderCarouselImageDefaults() {
	return normalizeHeaderCarouselData({
		variant: HEADER_CAROUSEL_VARIANT_IMAGE,
		items: createDefaultHeaderCarouselSlides(2, HEADER_CAROUSEL_VARIANT_IMAGE),
		dotColor: '#b3b3b3',
		dotActiveColor: '#ffffff',
	});
}

/** @deprecated 使用 getHeaderCarouselBannerDefaults */
export function getHeaderCarouselDefaults() {
	return getHeaderCarouselBannerDefaults();
}

export function getHeaderCarouselDefaultsByComponentType(type) {
	if (type === HEADER_CAROUSEL_IMAGE_COMPONENT_TYPE) {
		return getHeaderCarouselImageDefaults();
	}
	return getHeaderCarouselBannerDefaults();
}

export function isHeaderCarouselComponent(type) {
	return type === HEADER_CAROUSEL_COMPONENT_TYPE
		|| type === HEADER_CAROUSEL_IMAGE_COMPONENT_TYPE;
}

export function getHeaderCarouselImageUrl(image) {
	if (!image) return '';
	return getQiniuSignedUrl(image.previewUrl || image.url || '');
}

function normalizeSlideImage(image, fallback) {
	if (!image?.previewUrl && !image?.url) {
		return fallback ? { ...fallback } : createSlideImage();
	}
	return normalizeStoredImageItem(image, fallback || createSlideImage());
}

function normalizeSlide(slide, fallback) {
	const base = fallback || createDefaultHeaderCarouselSlide();
	const jumpLink = slide?.jumpLink?.type ? { ...slide.jumpLink } : null;
	return {
		id: slide?.id || uuidv4(),
		image: normalizeSlideImage(slide?.image, base.image),
		jumpLink,
		title: typeof slide?.title === 'string' ? slide.title : (base.title || ''),
		subtitle: typeof slide?.subtitle === 'string' ? slide.subtitle : (base.subtitle || ''),
	};
}

export function normalizeHeaderCarouselData(data = {}) {
	const variant = data.variant === HEADER_CAROUSEL_VARIANT_IMAGE
		? HEADER_CAROUSEL_VARIANT_IMAGE
		: HEADER_CAROUSEL_VARIANT_BANNER;
	const fallbackSlides = createDefaultHeaderCarouselSlides(1, variant);
	const sourceItems = Array.isArray(data.items)
		? data.items
		: (Array.isArray(data.slides) ? data.slides : []);
	const slides = sourceItems.length
		? sourceItems.map((slide, index) => normalizeSlide(slide, fallbackSlides[index % fallbackSlides.length]))
		: fallbackSlides;

	return {
		variant,
		items: slides.slice(0, HEADER_CAROUSEL_MAX_SLIDES),
		borderRadius: Math.max(0, Number(data.borderRadius) || HEADER_CAROUSEL_DEFAULT_BORDER_RADIUS),
		height: Math.max(80, Number(data.height) || HEADER_CAROUSEL_DEFAULT_HEIGHT),
		autoplay: data.autoplay !== false,
		autoplayInterval: Math.max(
			1000,
			Number(data.autoplayInterval) || HEADER_CAROUSEL_DEFAULT_AUTOPLAY_INTERVAL,
		),
		titleColor: normalizeHexColor(data.titleColor || DEFAULT_TITLE_COLOR),
		subtitleColor: normalizeHexColor(data.subtitleColor || DEFAULT_SUBTITLE_COLOR),
		dotColor: normalizeHexColor(data.dotColor || DEFAULT_DOT_COLOR),
		dotActiveColor: normalizeHexColor(data.dotActiveColor || DEFAULT_DOT_ACTIVE_COLOR),
	};
}

export function revokeHeaderCarouselImages(data) {
	(data?.items || data?.slides || []).forEach((slide) => {
		revokeLocalImageUrl(slide?.image?.previewUrl);
	});
}
